import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

const PACKAGING_FEE = 10000; // 100.00 MXN in cents
const MAX_ITEMS = 3;
const MAMA_DISCOUNT_RATE = 0.15; // 15% off
let bundleCounter = 0;

export class BundleBuilderComponent extends Component {
  /** @type {Map<string, {price: number, title: string, type: string}>} */
  #selectedItems = new Map();
  #selectedBag = '';
  #isSubmitting = false;
  #diffuserToastShown = false;
  #hasMamaDiscount = false;

  connectedCallback() {
    super.connectedCallback();
    this.#initScrollbars();
    this.#checkCampaignBanner();
  }

  #checkCampaignBanner() {
    const params = new URLSearchParams(window.location.search);
    const campaign = params.get('campaign');

    // Persist the discount flag in sessionStorage so it survives page reloads within the session
    if (campaign === 'mama') {
      sessionStorage.setItem('amanthi-mama-discount-active', 'true');
    }

    this.#hasMamaDiscount = sessionStorage.getItem('amanthi-mama-discount-active') === 'true';

    const dismissed = localStorage.getItem('amanthi-mama-banner-dismissed');

    if (this.#hasMamaDiscount && dismissed !== 'true') {
      if (this.refs.campaignBanner) {
        this.refs.campaignBanner.style.display = 'block';
        requestAnimationFrame(() => {
          this.refs.campaignBanner.dataset.visible = 'true';
        });
      }
    }

    // Recalculate summary with discount
    this.#updateSummary();
  }

  closeCampaignBanner() {
    if (this.refs.campaignBanner) {
      this.refs.campaignBanner.dataset.visible = 'false';
      setTimeout(() => {
        this.refs.campaignBanner.style.display = 'none';
      }, 300);
      localStorage.setItem('amanthi-mama-banner-dismissed', 'true');
    }
  }

  #initScrollbars() {
    const grids = this.querySelectorAll('.bundle-category__grid');
    grids.forEach((grid) => {
      const scrollbar = grid.parentElement?.querySelector('.bundle-category__scrollbar-thumb');
      if (!scrollbar) return;

      const updateThumb = () => {
        const { scrollLeft, scrollWidth, clientWidth } = grid;
        const maxScroll = scrollWidth - clientWidth;
        if (maxScroll <= 0) {
          scrollbar.parentElement.style.display = 'none';
          return;
        }
        const progress = scrollLeft / maxScroll;
        const thumbWidth = (clientWidth / scrollWidth) * 100;
        const thumbOffset = progress * (100 - thumbWidth);
        scrollbar.style.width = `${thumbWidth}%`;
        scrollbar.style.transform = `translateX(${(thumbOffset / thumbWidth) * 100}%)`;
      };

      grid.addEventListener('scroll', updateThumb, { passive: true });
      updateThumb();
    });
  }

  /**
   * Select bag color
   * @param {Event} event
   */
  selectBag(event) {
    const btn = event.target.closest('.bundle-bag-option');
    if (!btn) return;

    this.#selectedBag = btn.dataset.bag;

    // Update UI — deselect all, select clicked
    this.querySelectorAll('.bundle-bag-option').forEach((el) => {
      el.dataset.selected = 'false';
    });
    btn.dataset.selected = 'true';

    this.#updateSummary();
  }

  /**
   * Toggle product selection when a card is clicked
   * @param {Event} event
   */
  toggleProduct(event) {
    const card = event.target.closest('.bundle-product-card');
    if (!card || card.dataset.soldOut === 'true') return;

    // Must select bag first
    if (!this.#selectedBag) {
      this.#showToast('Primero selecciona tu bolsa de regalo');
      // Scroll to bag selector
      const bagSection = this.querySelector('.bundle-bag-selector');
      if (bagSection) {
        bagSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    const variantId = card.dataset.variantId;
    const price = parseInt(card.dataset.price, 10);
    const title = card.dataset.title;
    const type = card.dataset.productType || '';

    if (this.#selectedItems.has(variantId)) {
      this.#selectedItems.delete(variantId);
      card.dataset.selected = 'false';
    } else {
      // Enforce max limit
      if (this.#selectedItems.size >= MAX_ITEMS) {
        this.#showError(`Maximo ${MAX_ITEMS} productos por kit`);
        return;
      }
      this.#selectedItems.set(variantId, { price, title, type });
      card.dataset.selected = 'true';
    }

    this.#updateSummary();
  }

  #updateSummary() {
    const count = this.#selectedItems.size;
    let subtotal = 0;
    for (const { price } of this.#selectedItems.values()) {
      subtotal += price;
    }

    const moneyFormat = this.dataset.moneyFormat;
    const currency = this.dataset.currency;

    // Packaging fee logic
    let packagingFee = 0;
    let packagingText = '';
    let packagingPriceText = '';
    let showPackaging = false;

    if (count === 1) {
      packagingFee = PACKAGING_FEE;
      packagingText = 'Empaque de regalo';
      packagingPriceText = formatMoney(PACKAGING_FEE, moneyFormat, currency);
      showPackaging = true;
    } else if (count >= 2) {
      packagingText = 'Empaque de regalo';
      packagingPriceText = `<s>${formatMoney(PACKAGING_FEE, moneyFormat, currency)}</s> Gratis`;
      showPackaging = true;
    }

    // Apply Mother's Day discount (15% off products only, not packaging)
    let discountAmount = 0;
    const showDiscount = this.#hasMamaDiscount && count > 0;
    if (showDiscount) {
      discountAmount = Math.round(subtotal * MAMA_DISCOUNT_RATE);
    }

    const total = subtotal + packagingFee - discountAmount;

    // Update bag line in summary
    if (this.refs.summaryBag) {
      this.refs.summaryBag.style.display = this.#selectedBag ? 'flex' : 'none';
    }
    if (this.refs.summaryBagColor) {
      this.refs.summaryBagColor.textContent = this.#selectedBag ? `Bolsa ${this.#selectedBag}` : '';
    }

    // Update count text
    if (this.refs.summaryCount) {
      if (count === 0) {
        this.refs.summaryCount.textContent = `Selecciona hasta ${MAX_ITEMS} productos`;
      } else {
        this.refs.summaryCount.textContent = `${count} de ${MAX_ITEMS} productos`;
      }
    }

    // Update selected items list
    if (this.refs.summaryItems) {
      if (count === 0) {
        this.refs.summaryItems.innerHTML = '';
      } else {
        const itemsHtml = Array.from(this.#selectedItems.values())
          .map(({ title, price }) =>
            `<div class="bundle-summary__item">
              <span class="bundle-summary__item-title">${title}</span>
              <span class="bundle-summary__item-price">${formatMoney(price, moneyFormat, currency)}</span>
            </div>`
          )
          .join('');
        this.refs.summaryItems.innerHTML = itemsHtml;
      }
    }

    if (this.refs.summarySubtotal) {
      this.refs.summarySubtotal.textContent = formatMoney(subtotal, moneyFormat, currency);
    }
    if (this.refs.packagingLine) {
      this.refs.packagingLine.style.display = showPackaging ? 'flex' : 'none';
    }
    if (this.refs.packagingLabel) {
      this.refs.packagingLabel.textContent = packagingText;
    }
    if (this.refs.packagingPrice) {
      this.refs.packagingPrice.innerHTML = packagingPriceText;
    }
    if (this.refs.discountLine) {
      this.refs.discountLine.style.display = showDiscount ? 'flex' : 'none';
    }
    if (this.refs.discountAmount) {
      this.refs.discountAmount.textContent = showDiscount ? `-${formatMoney(discountAmount, moneyFormat, currency)}` : '';
    }
    if (this.refs.summaryTotal) {
      this.refs.summaryTotal.textContent = formatMoney(total, moneyFormat, currency);
    }

    // Update desktop button
    if (this.refs.addToCartButton) {
      this.refs.addToCartButton.disabled = count === 0 || !this.#selectedBag;
    }

    // Update mobile bar
    if (this.refs.mobileBar) {
      this.refs.mobileBar.dataset.visible = count > 0 ? 'true' : 'false';
    }
    if (this.refs.mobileCount) {
      this.refs.mobileCount.textContent = `${count} de ${MAX_ITEMS} productos`;
    }
    if (this.refs.mobileTotal) {
      this.refs.mobileTotal.textContent = formatMoney(total, moneyFormat, currency);
    }
    if (this.refs.mobileAddToCartButton) {
      this.refs.mobileAddToCartButton.disabled = count === 0 || !this.#selectedBag;
    }

    // Mobile items list
    if (this.refs.mobileItems) {
      if (count === 0) {
        this.refs.mobileItems.innerHTML = '<p class="bundle-mobile-bar__empty-text">Selecciona hasta 3 productos</p>';
      } else {
        this.refs.mobileItems.innerHTML = Array.from(this.#selectedItems.values())
          .map(({ title, price }) =>
            `<div class="bundle-mobile-bar__item">
              <span class="bundle-mobile-bar__item-title">${title}</span>
              <span class="bundle-mobile-bar__item-price">${formatMoney(price, moneyFormat, currency)}</span>
            </div>`
          )
          .join('');
      }
    }

    // Mobile subtotal & packaging
    if (this.refs.mobileSubtotal) {
      this.refs.mobileSubtotal.textContent = formatMoney(subtotal, moneyFormat, currency);
    }
    if (this.refs.mobilePackagingLine) {
      this.refs.mobilePackagingLine.style.display = showPackaging ? 'flex' : 'none';
    }
    if (this.refs.mobilePackagingPrice) {
      this.refs.mobilePackagingPrice.innerHTML = packagingPriceText;
    }
    if (this.refs.mobileDiscountLine) {
      this.refs.mobileDiscountLine.style.display = showDiscount ? 'flex' : 'none';
    }
    if (this.refs.mobileDiscountAmount) {
      this.refs.mobileDiscountAmount.textContent = showDiscount ? `-${formatMoney(discountAmount, moneyFormat, currency)}` : '';
    }
    if (this.refs.mobileTotalDetail) {
      this.refs.mobileTotalDetail.textContent = formatMoney(total, moneyFormat, currency);
    }

    // Mobile diffuser warning
    if (this.refs.mobileDiffuserWarning) {
      const mobileTypes = Array.from(this.#selectedItems.values()).map((item) => item.type);
      const mobileHasDiffuser = mobileTypes.includes('Difusor');
      const mobileHasOil = mobileTypes.includes('Aceite Esencial');
      this.refs.mobileDiffuserWarning.style.display = mobileHasDiffuser && !mobileHasOil ? 'block' : 'none';
    }

    // Diffuser without essential oil warning
    const types = Array.from(this.#selectedItems.values()).map((item) => item.type);
    const hasDiffuser = types.includes('Difusor');
    const hasOil = types.includes('Aceite Esencial');
    const showDiffuserWarning = hasDiffuser && !hasOil;

    if (this.refs.diffuserWarning) {
      this.refs.diffuserWarning.style.display = showDiffuserWarning ? 'block' : 'none';
    }

    // Show toast on mobile when diffuser is first added without oil
    if (showDiffuserWarning && !this.#diffuserToastShown) {
      this.#diffuserToastShown = true;
      this.#showToast('El difusor no incluye aceite esencial. Te recomendamos agregar uno.', 'warning');
    }
    if (!hasDiffuser || hasOil) {
      this.#diffuserToastShown = false;
    }

    // Hide error when selection changes
    if (this.refs.errorMessage) {
      this.refs.errorMessage.classList.add('hidden');
    }
  }

  /**
   * Add all selected items to cart
   * @param {Event} event
   */
  async addToCart(event) {
    if (this.#isSubmitting || this.#selectedItems.size === 0) return;

    if (!this.#selectedBag) {
      this.#showError('Primero selecciona una bolsa');
      return;
    }

    this.#isSubmitting = true;
    this.#setButtonsLoading(true);

    // Generate unique bundle ID for this kit
    bundleCounter++;
    const bundleLabel = `Kit de Regalo #${bundleCounter}`;
    const bundleId = `kit-${Date.now()}`;

    // Build product names list for the bundle
    const productNames = Array.from(this.#selectedItems.values())
      .map(({ title }) => title)
      .join(', ');

    const bundleProperties = {
      '_bundle_id': bundleId,
      'Kit de Regalo': bundleLabel,
      'Bolsa': `${this.#selectedBag}`,
      'Productos del kit': productNames,
    };

    const items = Array.from(this.#selectedItems.entries()).map(([variantId]) => ({
      id: Number(variantId),
      quantity: 1,
      properties: { ...bundleProperties },
    }));

    // Add packaging product — paid ($100) for 1 item, free ($0) for 2+
    const useFreePacking = this.#selectedItems.size >= 2;
    const packagingVariantId = useFreePacking
      ? this.dataset.packagingFreeVariantId
      : this.dataset.packagingVariantId;

    if (packagingVariantId) {
      items.push({
        id: Number(packagingVariantId),
        quantity: 1,
        properties: { ...bundleProperties },
      });
    }

    // Collect section IDs for cart drawer update
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });

    const payload = { items };
    if (sectionIds.length > 0) {
      payload.sections = sectionIds.join(',');
    }

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.status) {
        this.#showError(data.message || 'Error al agregar al carrito');
      } else {
        document.dispatchEvent(
          new CartAddEvent({}, this.id, {
            source: 'bundle-builder',
            itemCount: items.length,
            sections: data.sections,
          })
        );
        this.#clearSelection();
      }
    } catch (error) {
      console.error('Bundle builder add to cart error:', error);
      this.#showError('Error al agregar al carrito. Intenta de nuevo.');
    } finally {
      this.#isSubmitting = false;
      this.#setButtonsLoading(false);
    }
  }

  /**
   * Slide modal gallery via arrows
   * @param {Event} event
   */
  slideModal(event) {
    event.stopPropagation();
    const btn = event.target.closest('.bundle-modal__arrow');
    if (!btn) return;

    const slider = btn.closest('.bundle-modal__slider');
    const slides = slider.querySelectorAll('.bundle-modal__slide');
    const dots = slider.querySelectorAll('.bundle-modal__dot');
    const total = slides.length;

    let current = 0;
    slides.forEach((s, i) => { if (s.classList.contains('bundle-modal__slide--active')) current = i; });

    const dir = btn.dataset.dir;
    let next = dir === 'next' ? current + 1 : current - 1;
    if (next >= total) next = 0;
    if (next < 0) next = total - 1;

    slides.forEach((s, i) => s.classList.toggle('bundle-modal__slide--active', i === next));
    dots.forEach((d, i) => d.classList.toggle('bundle-modal__dot--active', i === next));
  }

  /**
   * Slide modal gallery via dots
   * @param {Event} event
   */
  slideModalDot(event) {
    event.stopPropagation();
    const dot = event.target.closest('.bundle-modal__dot');
    if (!dot) return;

    const slider = dot.closest('.bundle-modal__slider');
    const index = parseInt(dot.dataset.slideIndex, 10);

    slider.querySelectorAll('.bundle-modal__slide').forEach((s, i) =>
      s.classList.toggle('bundle-modal__slide--active', i === index));
    slider.querySelectorAll('.bundle-modal__dot').forEach((d, i) =>
      d.classList.toggle('bundle-modal__dot--active', i === index));
  }

  /**
   * Show product info modal
   * @param {Event} event
   */
  showProductInfo(event) {
    event.stopPropagation();
    const btn = event.target.closest('.bundle-product-card__info-btn');
    if (!btn) return;

    const productId = btn.dataset.productId;
    const modal = this.querySelector(`.bundle-product-modal[data-modal-id="${productId}"]`);
    if (modal) {
      modal.style.display = 'flex';
      document.body.style.overflow = 'hidden';
    }
  }

  /**
   * Close product info modal
   * @param {Event} event
   */
  closeProductInfo(event) {
    event.stopPropagation();
    const modal = event.target.closest('.bundle-product-modal');
    if (modal) {
      modal.style.display = 'none';
      document.body.style.overflow = '';
    }
  }

  toggleMobilePanel() {
    if (!this.refs.mobileBar) return;
    const expanded = this.refs.mobileBar.dataset.expanded === 'true';
    this.refs.mobileBar.dataset.expanded = expanded ? 'false' : 'true';
  }

  #setButtonsLoading(loading) {
    const buttons = [this.refs.addToCartButton, this.refs.mobileAddToCartButton];
    for (const btn of buttons) {
      if (!btn) continue;
      btn.disabled = loading;
      if (loading) {
        btn.dataset.loading = 'true';
      } else {
        delete btn.dataset.loading;
      }
    }
  }

  #showToast(message, type = 'info') {
    // Remove existing toast if any
    const existing = this.querySelector('.bundle-toast');
    if (existing) existing.remove();

    const toast = document.createElement('div');
    toast.className = 'bundle-toast';
    if (type === 'warning') toast.classList.add('bundle-toast--warning');
    toast.textContent = message;
    this.appendChild(toast);

    // Trigger animation
    requestAnimationFrame(() => {
      toast.dataset.visible = 'true';
    });

    const duration = type === 'warning' ? 5000 : 3000;
    setTimeout(() => {
      toast.dataset.visible = 'false';
      setTimeout(() => toast.remove(), 300);
    }, duration);
  }

  #showError(message) {
    if (this.refs.errorMessage) {
      this.refs.errorMessage.textContent = message;
      this.refs.errorMessage.classList.remove('hidden');
    }
  }

  #clearSelection() {
    this.#selectedItems.clear();
    this.#selectedBag = '';
    const cards = this.querySelectorAll('.bundle-product-card[data-selected="true"]');
    cards.forEach((card) => {
      card.dataset.selected = 'false';
    });
    this.querySelectorAll('.bundle-bag-option').forEach((el) => {
      el.dataset.selected = 'false';
    });
    this.#updateSummary();
  }
}

customElements.define('bundle-builder-component', BundleBuilderComponent);
